import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import type { RootState } from '../../../../state/store';
import {
  fetchBusinessParameterCatalog,
  fetchBusinessParameterHistory,
  fetchBusinessParameters,
  fetchBusinessConfigLocations,
  fetchKpiTargetCatalog,
  fetchKpiTargetHistory,
  fetchKpiTargets,
  saveBusinessParameters,
  saveKpiTargets,
  type BusinessParameter,
  type BusinessParameterCatalog,
  type BusinessParameterCatalogEntry,
  type BusinessConfigLocation,
  type KpiTarget,
  type KpiTargetCatalog,
  type KpiTargetCatalogEntry,
} from '../../services/api';
import AskV2ThemeProvider from '../../theme/provider';
import { brandGradient } from '../../theme/constants';

interface Props {
  show: boolean;
  onClose: () => void;
  initialCategory?: string;
  initialFocus?: ProfileFocus;
}

type Mode = 'business_parameters' | 'kpi_targets';

interface ProfileFocus {
  mode?: Mode;
  category?: string;
  key?: string;
  metric_key?: string;
  period_type?: string;
  location_ids?: number[];
  location_name?: string;
}

/**
 * Central "KPIs and Business Parameters" panel. Tabs between the two modes;
 * within each, a category sidebar lists every known category with a completion
 * count, and the body renders one card per parameter. Each card shows the
 * parameter name as a heading with description, status chip, and a generous
 * editor area underneath.
 *
 * The "Effective from" field + Save button only appear once the user edits
 * the value (dirty-state reveal), keeping the default view calm.
 *
 * Catalogs are cached at module scope for the lifetime of the page so that
 * opening the modal a second time (or switching tabs) is instant. Values
 * loaded once are kept in component state and merged in place on save.
 */

// ── Module-level catalog cache (cheap, immutable for the session) ──────────

let bpCatalogCache: BusinessParameterCatalog | null = null;
let kpiCatalogCache: KpiTargetCatalog | null = null;
let catalogInflight: Promise<[BusinessParameterCatalog, KpiTargetCatalog]> | null = null;

async function loadCatalogs(): Promise<[BusinessParameterCatalog, KpiTargetCatalog]> {
  if (bpCatalogCache && kpiCatalogCache) return [bpCatalogCache, kpiCatalogCache];
  if (catalogInflight) return catalogInflight;
  catalogInflight = Promise.all([fetchBusinessParameterCatalog(), fetchKpiTargetCatalog()])
    .then(([bp, kpi]) => {
      bpCatalogCache = bp;
      kpiCatalogCache = kpi;
      return [bp, kpi] as [BusinessParameterCatalog, KpiTargetCatalog];
    })
    .finally(() => {
      catalogInflight = null;
    });
  return catalogInflight;
}

function categoryForFocus(
  focus: ProfileFocus,
  bpCatalog: BusinessParameterCatalog | null,
  kpiCatalog: KpiTargetCatalog | null
): string | undefined {
  if (focus.mode === 'kpi_targets' || focus.metric_key) {
    if (!focus.metric_key || !kpiCatalog) return undefined;
    return Object.entries(kpiCatalog.categories)
      .find(([, category]) => Object.prototype.hasOwnProperty.call(category.keys, focus.metric_key!))?.[0];
  }

  if (!focus.key || !bpCatalog) return undefined;
  return Object.entries(bpCatalog.categories)
    .find(([, category]) => Object.prototype.hasOwnProperty.call(category.keys, focus.key!))?.[0];
}

function locationIdsForFocus(
  focus: ProfileFocus,
  locations: BusinessConfigLocation[]
): number[] {
  const explicitIds = (focus.location_ids || [])
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0);
  if (explicitIds.length > 0) return explicitIds;

  const targetName = focus.location_name?.trim().toLowerCase();
  if (!targetName) return [];

  const match = locations.find((location) => location.name.trim().toLowerCase() === targetName) ||
    locations.find((location) => targetName.includes(location.name.trim().toLowerCase())) ||
    locations.find((location) => location.name.trim().toLowerCase().includes(targetName));

  return match ? [Number(match.id)] : [];
}

// ── Panel ──────────────────────────────────────────────────────────────────

export const BusinessProfilePanel: React.FC<Props> = ({ show, onClose, initialCategory, initialFocus }) => {
  const [mode, setMode] = useState<Mode>('business_parameters');
  const [bpCatalog, setBpCatalog] = useState<BusinessParameterCatalog | null>(bpCatalogCache);
  const [kpiCatalog, setKpiCatalog] = useState<KpiTargetCatalog | null>(kpiCatalogCache);
  const [bpValues, setBpValues] = useState<BusinessParameter[] | null>(null);
  const [kpiValues, setKpiValues] = useState<KpiTarget[] | null>(null);
  const [locations, setLocations] = useState<BusinessConfigLocation[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [viewLocationId, setViewLocationId] = useState<number | null>(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(initialCategory ?? null);
  const [appliedFocus, setAppliedFocus] = useState<ProfileFocus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const appliedFocusRef = useRef<string | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [[bpCat, kpiCat], availableLocations] = await Promise.all([
        loadCatalogs(),
        fetchBusinessConfigLocations(),
      ]);
      const normalizedLocations = availableLocations
        .map((location) => ({ ...location, id: Number(location.id) }))
        .filter((location) => Number.isFinite(location.id));
      setBpCatalog(bpCat);
      setKpiCatalog(kpiCat);
      setLocations(normalizedLocations);

      if (!activeCategory) {
        const first = Object.keys(bpCat.categories)[0];
        if (first) setActiveCategory(first);
      }

      const activeLocationId = viewLocationId ?? selectedLocationIds[0] ?? normalizedLocations[0]?.id;
      if (!activeLocationId) {
        setBpValues([]);
        setKpiValues([]);
        return;
      }
      if (!viewLocationId) setViewLocationId(activeLocationId);
      if (selectedLocationIds.length === 0) setSelectedLocationIds([activeLocationId]);

      const [bp, kpi] = await Promise.all([
        fetchBusinessParameters({ location_id: activeLocationId }),
        fetchKpiTargets({ location_id: activeLocationId }),
      ]);
      setBpValues(bp.parameters);
      setKpiValues(kpi.targets);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Failed to load your KPIs and Business Parameters.'
      );
    }
  }, [activeCategory, viewLocationId]);

  useEffect(() => {
    if (!show) return;
    void loadAll();
  }, [show, loadAll]);

  useEffect(() => {
    if (!show || !initialFocus) return;

    const signature = JSON.stringify({
      focus: initialFocus,
      catalogsReady: Boolean(bpCatalog && kpiCatalog),
      locationCount: locations.length,
    });
    if (appliedFocusRef.current === signature) return;
    appliedFocusRef.current = signature;

    const focusMode = initialFocus.mode || (initialFocus.metric_key ? 'kpi_targets' : initialFocus.key ? 'business_parameters' : undefined);
    if (focusMode) setMode(focusMode);

    const focusedCatalog = focusMode === 'kpi_targets' ? kpiCatalog : bpCatalog;
    const focusCategory = initialFocus.category && focusedCatalog?.categories[initialFocus.category]
      ? initialFocus.category
      : categoryForFocus(initialFocus, bpCatalog, kpiCatalog);
    if (focusCategory) setActiveCategory(focusCategory);

    setAppliedFocus({
      ...initialFocus,
      ...(focusMode ? { mode: focusMode } : {}),
      ...(focusCategory ? { category: focusCategory } : {}),
    });

    const locationIds = locationIdsForFocus(initialFocus, locations);
    if (locationIds.length > 0) {
      setSelectedLocationIds(locationIds);
      setViewLocationId(locationIds[0]);
      setBpValues(null);
      setKpiValues(null);
    }
  }, [show, initialFocus, bpCatalog, kpiCatalog, locations]);

  const mergeBp = useCallback((row: BusinessParameter) => {
    setBpValues((prev) => {
      const base = prev ? prev.filter((p) => p.key !== row.key) : [];
      return [row, ...base];
    });
  }, []);

  const mergeKpi = useCallback((row: KpiTarget) => {
    setKpiValues((prev) => {
      const base = prev
        ? prev.filter((t) => !(t.metric_key === row.metric_key && t.period_type === row.period_type))
        : [];
      return [row, ...base];
    });
  }, []);

  const bpByKey = useMemo(() => {
    const map = new Map<string, BusinessParameter>();
    (bpValues || []).forEach((p) => map.set(p.key, p));
    return map;
  }, [bpValues]);

  const kpiByKey = useMemo(() => {
    const map = new Map<string, KpiTarget[]>();
    (kpiValues || []).forEach((t) => {
      const list = map.get(t.metric_key) || [];
      list.push(t);
      map.set(t.metric_key, list);
    });
    return map;
  }, [kpiValues]);

  const activeCatalog: Record<
    string,
    { label: string; keys: Record<string, BusinessParameterCatalogEntry | KpiTargetCatalogEntry> }
  > = mode === 'business_parameters' ? bpCatalog?.categories ?? {} : kpiCatalog?.categories ?? {};

  const currentCategory =
    activeCategory && activeCatalog[activeCategory]
      ? activeCategory
      : Object.keys(activeCatalog)[0] ?? null;

  const activeLocationId = viewLocationId ?? selectedLocationIds[0] ?? null;
  const activeLocation = locations.find((location) => Number(location.id) === activeLocationId);

  const toggleLocation = useCallback((locationId: number) => {
    const normalizedLocationId = Number(locationId);
    setSelectedLocationIds((prev) => {
      if (prev.includes(normalizedLocationId)) {
        const next = prev.filter((id) => id !== normalizedLocationId);
        if (activeLocationId === normalizedLocationId && next.length > 0) {
          setViewLocationId(next[0]);
        }
        return next.length > 0 ? next : prev;
      }
      return [...prev, normalizedLocationId];
    });
  }, [activeLocationId]);

  const handleViewLocation = useCallback((locationId: number) => {
    const normalizedLocationId = Number(locationId);
    setViewLocationId(normalizedLocationId);
    setSelectedLocationIds((prev) => (prev.includes(normalizedLocationId) ? prev : [normalizedLocationId, ...prev]));
    setBpValues(null);
    setKpiValues(null);
  }, []);

  const selectAllLocations = useCallback(() => {
    setSelectedLocationIds(locations.map((location) => Number(location.id)));
  }, [locations]);

  const clearSelectedLocations = useCallback(() => {
    if (activeLocationId) setSelectedLocationIds([activeLocationId]);
  }, [activeLocationId]);

  const filteredLocations = useMemo(() => {
    const term = locationSearch.trim().toLowerCase();
    const visibleLocations = term
      ? locations.filter((location) => location.name.toLowerCase().includes(term))
      : locations;
    return [...visibleLocations].sort((a, b) => {
      const aSelected = selectedLocationIds.includes(Number(a.id));
      const bSelected = selectedLocationIds.includes(Number(b.id));
      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  }, [locations, locationSearch, selectedLocationIds]);

  const completionCount = (categoryKey: string) => {
    const entries = activeCatalog[categoryKey]?.keys || {};
    const keys = Object.keys(entries);
    const configured = keys.filter((k) =>
      mode === 'business_parameters' ? bpByKey.has(k) : (kpiByKey.get(k) || []).length > 0
    ).length;
    return { configured, total: keys.length };
  };

  const catalogReady = mode === 'business_parameters' ? !!bpCatalog : !!kpiCatalog;
  const valuesReady = mode === 'business_parameters' ? bpValues !== null : kpiValues !== null;
  const focusId =
    appliedFocus?.mode === 'business_parameters' && appliedFocus.key
      ? `bp:${appliedFocus.key}`
      : appliedFocus?.mode === 'kpi_targets' && appliedFocus.metric_key
        ? `kpi:${appliedFocus.metric_key}`
        : null;

  useEffect(() => {
    if (!show || !focusId || !valuesReady) return;

    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-av-profile-focus-id="${focusId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [show, focusId, valuesReady, currentCategory, mode]);

  return (
    <AskV2ThemeProvider>
      <ProfileModalStyles />
      <Modal
        show={show}
        onHide={onClose}
        centered
        size="xl"
        backdrop="static"
        dialogClassName="av2-profile-modal"
        contentClassName="av2-profile-modal-content"
      >
        <ModalPanel>
      <Header>
        <div>
          <Title>KPIs and Business Parameters</Title>
          <Subtitle>Configure the values Ask Vantage uses for your calculations.</Subtitle>
        </div>
        <CloseBtn onClick={onClose} aria-label="Close">×</CloseBtn>
      </Header>

      <ModeTabs>
        <ModeTab $active={mode === 'business_parameters'} onClick={() => setMode('business_parameters')}>
          Business parameters
        </ModeTab>
        <ModeTab $active={mode === 'kpi_targets'} onClick={() => setMode('kpi_targets')}>
          KPI targets
        </ModeTab>
      </ModeTabs>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Body>
        <CategoryNav>
          {catalogReady ? (
            Object.entries(activeCatalog).map(([catKey, cat]) => {
              const { configured, total } = completionCount(catKey);
              return (
                <CategoryItem
                  key={catKey}
                  $active={currentCategory === catKey}
                  onClick={() => setActiveCategory(catKey)}
                >
                  <span>{cat.label}</span>
                  <CompletionBadge $complete={configured === total}>
                    {configured} / {total}
                  </CompletionBadge>
                </CategoryItem>
              );
            })
          ) : (
            <SkeletonNav />
          )}
        </CategoryNav>

        <CategoryBody>
          {!catalogReady && <SkeletonBody />}

          {catalogReady && currentCategory && mode === 'business_parameters' && bpCatalog && (
            <BpCategoryEditor
              categoryKey={currentCategory}
              category={bpCatalog.categories[currentCategory]!}
              values={bpByKey}
              valuesReady={valuesReady}
              selectedLocationIds={selectedLocationIds}
              activeLocationId={activeLocationId}
              focusKey={appliedFocus?.mode === 'business_parameters' ? appliedFocus.key : undefined}
              onSaved={mergeBp}
            />
          )}

          {catalogReady && currentCategory && mode === 'kpi_targets' && kpiCatalog && (
            <KpiCategoryEditor
              categoryKey={currentCategory}
              category={kpiCatalog.categories[currentCategory]!}
              values={kpiByKey}
              valuesReady={valuesReady}
              selectedLocationIds={selectedLocationIds}
              activeLocationId={activeLocationId}
              focusMetricKey={appliedFocus?.mode === 'kpi_targets' ? appliedFocus.metric_key : undefined}
              focusPeriodType={appliedFocus?.mode === 'kpi_targets' ? appliedFocus.period_type : undefined}
              onSaved={mergeKpi}
            />
          )}
        </CategoryBody>

        <LocationScopePane>
          <LocationPaneHeader>
            <LocationPaneTitle>Location scope</LocationPaneTitle>
            <SelectedCount>{selectedLocationIds.length} selected</SelectedCount>
          </LocationPaneHeader>

          <LocationSearchInput
            type="search"
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            placeholder="Search locations..."
          />

          <LocationPaneSection>
            <LocationPaneLabel>Viewing values for</LocationPaneLabel>
            <ViewingLocationSelect
              value={activeLocationId ?? ''}
              onChange={(e) => handleViewLocation(Number(e.target.value))}
              disabled={locations.length === 0}
            >
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </ViewingLocationSelect>
            <LocationHint>
              Current values, history, and completion counts are shown for {activeLocation?.name || 'one location'}.
            </LocationHint>
          </LocationPaneSection>

          <LocationPaneSection>
            <LocationPaneRow>
              <LocationPaneLabel>Apply saves to</LocationPaneLabel>
              <LocationActions>
                <LocationAction type="button" onClick={selectAllLocations}>Select all</LocationAction>
                <LocationAction type="button" onClick={clearSelectedLocations}>Clear</LocationAction>
              </LocationActions>
            </LocationPaneRow>

            <LocationList>
              {filteredLocations.map((location) => (
                <LocationListItem key={location.id}>
                  <input
                    type="checkbox"
                    checked={selectedLocationIds.includes(Number(location.id))}
                    onChange={() => toggleLocation(Number(location.id))}
                  />
                  <span>{location.name}</span>
                </LocationListItem>
              ))}
              {filteredLocations.length === 0 && (
                <LocationEmpty>No locations match your search.</LocationEmpty>
              )}
            </LocationList>
          </LocationPaneSection>
        </LocationScopePane>
      </Body>
        </ModalPanel>
      </Modal>
    </AskV2ThemeProvider>
  );
};

// ── Business Parameter category editor ─────────────────────────────────────

interface BpCategoryProps {
  categoryKey: string;
  category: { label: string; keys: Record<string, BusinessParameterCatalogEntry> };
  values: Map<string, BusinessParameter>;
  valuesReady: boolean;
  selectedLocationIds: number[];
  activeLocationId: number | null;
  focusKey?: string;
  onSaved: (row: BusinessParameter) => void;
}

const BpCategoryEditor: React.FC<BpCategoryProps> = ({
  categoryKey,
  category,
  values,
  valuesReady,
  selectedLocationIds,
  activeLocationId,
  focusKey,
  onSaved,
}) => {
  return (
    <>
      <SectionTitle>{category.label}</SectionTitle>
      <CardStack>
        {Object.entries(category.keys).map(([key, entry]) => (
          <BpCard
            key={`${categoryKey}:${key}`}
            categoryKey={categoryKey}
            paramKey={key}
            entry={entry}
            current={values.get(key)}
            valuesReady={valuesReady}
            selectedLocationIds={selectedLocationIds}
            activeLocationId={activeLocationId}
            isHighlighted={focusKey === key}
            onSaved={onSaved}
          />
        ))}
      </CardStack>
    </>
  );
};

interface BpCardProps {
  categoryKey: string;
  paramKey: string;
  entry: BusinessParameterCatalogEntry;
  current?: BusinessParameter;
  valuesReady: boolean;
  selectedLocationIds: number[];
  activeLocationId: number | null;
  isHighlighted?: boolean;
  onSaved: (row: BusinessParameter) => void;
}

const BpCard: React.FC<BpCardProps> = ({
  categoryKey,
  paramKey,
  entry,
  current,
  valuesReady,
  selectedLocationIds,
  activeLocationId,
  isHighlighted = false,
  onSaved,
}) => {
  const currencyCode = useSelector(
    (state: RootState) => (state.company?.company_details as { currency?: string })?.currency || ''
  );
  const [value, setValue] = useState<string>(() => initialBpInputValue(entry, current));
  const [mapValue, setMapValue] = useState<Record<string, string>>(() => initialBpMapValue(entry, current));
  const [effectiveFrom, setEffectiveFrom] = useState<string>(todayIso);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<BusinessParameter[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    setValue(initialBpInputValue(entry, current));
    setMapValue(initialBpMapValue(entry, current));
    setEffectiveFrom(todayIso);
  }, [entry, current]);

  const initialValue = initialBpInputValue(entry, current);
  const initialMap = initialBpMapValue(entry, current);
  const isDirty =
    entry.value_type === 'map'
      ? !shallowEqualStringMap(mapValue, initialMap)
      : value !== initialValue;

  const handleReset = useCallback(() => {
    setValue(initialValue);
    setMapValue(initialMap);
    setErrorMsg(null);
    setEffectiveFrom(todayIso);
  }, [initialValue, initialMap]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetchBusinessParameterHistory({
        category: categoryKey,
        key: paramKey,
        location_id: activeLocationId,
      });
      setHistory(res.history);
    } finally {
      setHistoryLoading(false);
    }
  }, [categoryKey, paramKey, activeLocationId]);

  const toggleHistory = useCallback(() => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history === null) void loadHistory();
  }, [historyOpen, history, loadHistory]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      if (selectedLocationIds.length === 0) throw new Error('Choose at least one location.');
      let payloadValue: number | string | Record<string, number>;
      if (entry.value_type === 'numeric') {
        const num = Number(value);
        if (!Number.isFinite(num)) throw new Error('Enter a number.');
        payloadValue = num;
      } else if (entry.value_type === 'enum') {
        if (!value) throw new Error('Choose a value.');
        payloadValue = value;
      } else {
        const map: Record<string, number> = {};
        Object.entries(mapValue).forEach(([k, v]) => {
          const num = Number(v);
          if (v !== '' && Number.isFinite(num)) map[k] = num;
        });
        if (Object.keys(map).length === 0) throw new Error('Enter at least one value.');
        payloadValue = map;
      }

      const res = await saveBusinessParameters(
        selectedLocationIds.map((locationId) => ({
          category: categoryKey,
          key: paramKey,
          value: payloadValue,
          location_id: locationId,
        })),
        { effective_from: effectiveFrom }
      );
      const row = res.created.find((created) => created.location_id === activeLocationId) || res.created[0];
      if (row) onSaved(row);
      setHistory(null);
      if (historyOpen) void loadHistory();
    } catch (err: unknown) {
      const maybe = err as { response?: { data?: { errors?: string[] } } } & Error;
      setErrorMsg(maybe.response?.data?.errors?.join(', ') || maybe.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [categoryKey, paramKey, entry.value_type, value, mapValue, selectedLocationIds, activeLocationId, effectiveFrom, historyOpen, loadHistory, onSaved]);

  const configured = !!current;

  return (
    <Card $highlight={isHighlighted} data-av-profile-focus-id={`bp:${paramKey}`}>
      <CardHeader>
        <CardHeading>{entry.label}</CardHeading>
        <StatusChip $active={configured}>
          <Dot $active={configured} />
          {configured ? 'Set' : 'Not set'}
        </StatusChip>
      </CardHeader>
      <CardDescription>{entry.description}</CardDescription>

      <CardEditor>
        {entry.value_type === 'numeric' && (
          <FieldGroup>
            <FieldLabel>Value</FieldLabel>
            <InputWithAffix>
              {entry.unit === 'currency' && currencyCode && <Affix $side="left">{currencyCode}</Affix>}
              <NumberInput
                type="number"
                step="any"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={entry.unit === 'currency' ? '0.00' : '0'}
                disabled={!valuesReady}
                $hasLeft={entry.unit === 'currency' && !!currencyCode}
                $hasRight={isPercentUnit(entry.unit)}
              />
              {isPercentUnit(entry.unit) && <Affix $side="right">%</Affix>}
              {entry.unit && entry.unit !== 'currency' && !isPercentUnit(entry.unit) && (
                <UnitHint>{entry.unit}</UnitHint>
              )}
            </InputWithAffix>
          </FieldGroup>
        )}

        {entry.value_type === 'enum' && (
          <FieldGroup>
            <FieldLabel>Value</FieldLabel>
            <Select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={!valuesReady}
            >
              <option value="">Select…</option>
              {(entry.enum_values || []).map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </Select>
          </FieldGroup>
        )}

        {entry.value_type === 'map' && (
          <FieldGroup>
            <FieldLabel>Breakdown{entry.unit ? ` (${entry.unit})` : ''}</FieldLabel>
            <MapGrid>
              {(entry.map_keys || []).map((mk) => (
                <MapRow key={mk}>
                  <MapRowLabel>{mk}</MapRowLabel>
                  <InputWithAffix>
                    <NumberInput
                      type="number"
                      step="any"
                      value={mapValue[mk] ?? ''}
                      onChange={(e) => setMapValue({ ...mapValue, [mk]: e.target.value })}
                      placeholder="0"
                      disabled={!valuesReady}
                      $hasRight={isPercentUnit(entry.unit)}
                    />
                    {isPercentUnit(entry.unit) && <Affix $side="right">%</Affix>}
                  </InputWithAffix>
                </MapRow>
              ))}
            </MapGrid>
          </FieldGroup>
        )}
      </CardEditor>

      {isDirty && (
        <DirtyFooter>
          <FieldGroup $inline>
            <FieldLabel>Effective from</FieldLabel>
            <DateInput
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              disabled={!valuesReady}
            />
          </FieldGroup>
          <DirtyActions>
            <ResetBtn type="button" onClick={handleReset} disabled={saving}>
              Reset
            </ResetBtn>
            <SaveBtn onClick={handleSave} disabled={saving || !valuesReady}>
              {saving ? 'Saving…' : 'Save'}
            </SaveBtn>
          </DirtyActions>
        </DirtyFooter>
      )}

      {errorMsg && <CardError>{errorMsg}</CardError>}

      <CardFooterLine>
        {!valuesReady ? (
          <CurrentValueSkeleton />
        ) : current ? (
          <CurrentValue>
            <CurrentLabel>Current:</CurrentLabel>{' '}
            <CurrentNumber>{formatBpValue(current.value)}</CurrentNumber>{' '}
            <CurrentMeta>· active from {formatDate(current.effective_from)}</CurrentMeta>
          </CurrentValue>
        ) : (
          <CurrentValueMuted>Not configured yet.</CurrentValueMuted>
        )}
        {current && (
          <HistoryToggle onClick={toggleHistory}>
            {historyOpen ? 'Hide history' : 'View history'}
          </HistoryToggle>
        )}
      </CardFooterLine>

      {historyOpen && (
        <HistoryWrap>
          {historyLoading && <HistoryLoading>Loading history…</HistoryLoading>}
          {history && (
            <HistoryList
              rows={history.map((r) => ({
                effective_from: r.effective_from,
                valueLabel: formatBpValue(r.value),
              }))}
            />
          )}
        </HistoryWrap>
      )}
    </Card>
  );
};

// ── KPI Target category editor ─────────────────────────────────────────────

interface KpiCategoryProps {
  categoryKey: string;
  category: { label: string; keys: Record<string, KpiTargetCatalogEntry> };
  values: Map<string, KpiTarget[]>;
  valuesReady: boolean;
  selectedLocationIds: number[];
  activeLocationId: number | null;
  focusMetricKey?: string;
  focusPeriodType?: string;
  onSaved: (row: KpiTarget) => void;
}

const KpiCategoryEditor: React.FC<KpiCategoryProps> = ({
  categoryKey,
  category,
  values,
  valuesReady,
  selectedLocationIds,
  activeLocationId,
  focusMetricKey,
  focusPeriodType,
  onSaved,
}) => {
  return (
    <>
      <SectionTitle>{category.label}</SectionTitle>
      <CardStack>
        {Object.entries(category.keys).map(([metricKey, entry]) => (
          <KpiCard
            key={`${categoryKey}:${metricKey}`}
            categoryKey={categoryKey}
            metricKey={metricKey}
            entry={entry}
            current={values.get(metricKey) || []}
            valuesReady={valuesReady}
            selectedLocationIds={selectedLocationIds}
            activeLocationId={activeLocationId}
            isHighlighted={focusMetricKey === metricKey}
            focusPeriodType={focusMetricKey === metricKey ? focusPeriodType : undefined}
            onSaved={onSaved}
          />
        ))}
      </CardStack>
    </>
  );
};

interface KpiCardProps {
  categoryKey: string;
  metricKey: string;
  entry: KpiTargetCatalogEntry;
  current: KpiTarget[];
  valuesReady: boolean;
  selectedLocationIds: number[];
  activeLocationId: number | null;
  isHighlighted?: boolean;
  focusPeriodType?: string;
  onSaved: (row: KpiTarget) => void;
}

const KpiCard: React.FC<KpiCardProps> = ({
  categoryKey,
  metricKey,
  entry,
  current,
  valuesReady,
  selectedLocationIds,
  activeLocationId,
  isHighlighted = false,
  focusPeriodType,
  onSaved,
}) => {
  const currencyCode = useSelector(
    (state: RootState) => (state.company?.company_details as { currency?: string })?.currency || ''
  );
  const defaultPeriod = entry.period_types[0] ?? 'monthly';
  const [period, setPeriod] = useState<KpiTarget['period_type']>(defaultPeriod);
  const [value, setValue] = useState<string>('');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(todayIso);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<KpiTarget[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!focusPeriodType || !entry.period_types.includes(focusPeriodType as KpiTarget['period_type'])) return;
    setPeriod(focusPeriodType as KpiTarget['period_type']);
  }, [entry.period_types, focusPeriodType]);

  const currentByPeriod = useMemo(() => {
    const m = new Map<KpiTarget['period_type'], KpiTarget>();
    current.forEach((t) => m.set(t.period_type, t));
    return m;
  }, [current]);

  const currentForPeriod = currentByPeriod.get(period);
  const initialValue = currentForPeriod ? String(currentForPeriod.value) : '';
  const isDirty = value !== initialValue;

  useEffect(() => {
    setValue(initialValue);
    setHistory(null);
    setHistoryOpen(false);
    setEffectiveFrom(todayIso);
  }, [initialValue]);

  const handleReset = useCallback(() => {
    setValue(initialValue);
    setErrorMsg(null);
    setEffectiveFrom(todayIso);
  }, [initialValue]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetchKpiTargetHistory({
        category: categoryKey,
        metric_key: metricKey,
        period_type: period,
        location_id: activeLocationId,
      });
      setHistory(res.history);
    } finally {
      setHistoryLoading(false);
    }
  }, [categoryKey, metricKey, period, activeLocationId]);

  const toggleHistory = useCallback(() => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history === null) void loadHistory();
  }, [historyOpen, history, loadHistory]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      if (selectedLocationIds.length === 0) throw new Error('Choose at least one location.');
      const num = Number(value);
      if (!Number.isFinite(num)) throw new Error('Enter a number.');
      const res = await saveKpiTargets(
        selectedLocationIds.map((locationId) => ({
          category: categoryKey,
          metric_key: metricKey,
          period_type: period,
          value: num,
          location_id: locationId,
        })),
        { effective_from: effectiveFrom }
      );
      const row = res.created.find((created) => created.location_id === activeLocationId) || res.created[0];
      if (row) onSaved(row);
      setHistory(null);
      if (historyOpen) void loadHistory();
    } catch (err: unknown) {
      const maybe = err as { response?: { data?: { errors?: string[] } } } & Error;
      setErrorMsg(maybe.response?.data?.errors?.join(', ') || maybe.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [categoryKey, metricKey, period, value, selectedLocationIds, activeLocationId, effectiveFrom, historyOpen, loadHistory, onSaved]);

  const configuredPeriods = entry.period_types.filter((p) => currentByPeriod.has(p));
  const totalPeriods = entry.period_types.length;

  return (
    <Card $highlight={isHighlighted} data-av-profile-focus-id={`kpi:${metricKey}`}>
      <CardHeader>
        <CardHeading>{entry.label}</CardHeading>
        <StatusChip $active={configuredPeriods.length > 0}>
          <Dot $active={configuredPeriods.length > 0} />
          {configuredPeriods.length === totalPeriods
            ? 'All periods set'
            : `${configuredPeriods.length} of ${totalPeriods} set`}
        </StatusChip>
      </CardHeader>
      <CardDescription>{entry.description}</CardDescription>

      <CardEditor>
        {entry.period_types.length > 1 && (
          <FieldGroup>
            <FieldLabel>Period</FieldLabel>
            <Segmented role="tablist">
              {entry.period_types.map((p) => (
                <SegmentedButton
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={period === p}
                  $active={period === p}
                  onClick={() => setPeriod(p)}
                  disabled={!valuesReady}
                >
                  <span>{capitalize(p)}</span>
                  {currentByPeriod.has(p) && <SegmentedDot $active={period === p} />}
                </SegmentedButton>
              ))}
            </Segmented>
          </FieldGroup>
        )}

        <FieldGroup>
          <FieldLabel>Target value{entry.unit ? ` (${entry.unit})` : ''}</FieldLabel>
          <InputWithAffix>
            {entry.unit === 'currency' && currencyCode && <Affix $side="left">{currencyCode}</Affix>}
            <NumberInput
              type="number"
              step="any"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0"
              disabled={!valuesReady}
              $hasLeft={entry.unit === 'currency' && !!currencyCode}
              $hasRight={isPercentUnit(entry.unit)}
            />
            {isPercentUnit(entry.unit) && <Affix $side="right">%</Affix>}
          </InputWithAffix>
        </FieldGroup>
      </CardEditor>

      {isDirty && (
        <DirtyFooter>
          <FieldGroup $inline>
            <FieldLabel>Effective from</FieldLabel>
            <DateInput
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              disabled={!valuesReady}
            />
          </FieldGroup>
          <DirtyActions>
            <ResetBtn type="button" onClick={handleReset} disabled={saving}>
              Reset
            </ResetBtn>
            <SaveBtn onClick={handleSave} disabled={saving || !valuesReady}>
              {saving ? 'Saving…' : 'Save'}
            </SaveBtn>
          </DirtyActions>
        </DirtyFooter>
      )}

      {errorMsg && <CardError>{errorMsg}</CardError>}

      {valuesReady && entry.period_types.length > 1 && (
        <PeriodOverview>
          {entry.period_types.map((p) => {
            const t = currentByPeriod.get(p);
            return (
              <PeriodChip
                key={p}
                type="button"
                $active={period === p}
                $set={!!t}
                onClick={() => setPeriod(p)}
                disabled={!valuesReady}
              >
                <PeriodChipLabel>{capitalize(p)}</PeriodChipLabel>
                <PeriodChipValue>{t ? formatKpiValue(t.value, entry.unit, currencyCode) : '—'}</PeriodChipValue>
              </PeriodChip>
            );
          })}
        </PeriodOverview>
      )}

      <CardFooterLine>
        {!valuesReady ? (
          <CurrentValueSkeleton />
        ) : currentForPeriod ? (
          <CurrentValue>
            <CurrentLabel>Current ({currentForPeriod.period_type}):</CurrentLabel>{' '}
            <CurrentNumber>{formatKpiValue(currentForPeriod.value, entry.unit, currencyCode)}</CurrentNumber>{' '}
            <CurrentMeta>· active from {formatDate(currentForPeriod.effective_from)}</CurrentMeta>
          </CurrentValue>
        ) : (
          <CurrentValueMuted>Not configured for {period} yet.</CurrentValueMuted>
        )}
        {currentForPeriod && (
          <HistoryToggle onClick={toggleHistory}>
            {historyOpen ? 'Hide history' : 'View history'}
          </HistoryToggle>
        )}
      </CardFooterLine>

      {historyOpen && (
        <HistoryWrap>
          {historyLoading && <HistoryLoading>Loading history…</HistoryLoading>}
          {history && (
            <HistoryList
              rows={history.map((r) => ({
                effective_from: r.effective_from,
                valueLabel: formatKpiValue(r.value, entry.unit, currencyCode),
              }))}
            />
          )}
        </HistoryWrap>
      )}
    </Card>
  );
};

// ── History list (Past / Active / Upcoming) ────────────────────────────────

interface HistoryRow {
  effective_from: string;
  valueLabel: string;
}

/**
 * Renders an append-only history as three sections:
 *   - Upcoming: effective_from > today (future)
 *   - Active:   the single latest row with effective_from <= today
 *   - Past:     superseded rows
 *
 * Each row shows a validity span computed from the neighbour rows in the same
 * scope: e.g. `Mar 1 – Apr 14, 2026`, `Apr 15, 2026 – present`, `from May 1, 2026`.
 */
const HistoryList: React.FC<{ rows: HistoryRow[] }> = ({ rows }) => {
  if (rows.length === 0) return <HistoryEmpty>No history yet.</HistoryEmpty>;

  const asc = [...rows].sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
  const today = todayIso;

  const pastOrActive = asc.filter((r) => r.effective_from <= today);
  const future = asc.filter((r) => r.effective_from > today);
  const activeIndex = pastOrActive.length - 1;

  const buildSpan = (rowIdx: number, all: HistoryRow[]): string => {
    const row = all[rowIdx];
    const next = all[rowIdx + 1];
    if (!next) {
      if (row.effective_from > today) return `from ${formatDate(row.effective_from)}`;
      return `${formatDate(row.effective_from)} – present`;
    }
    const endDateIso = addDaysIso(next.effective_from, -1);
    return `${formatDate(row.effective_from)} – ${formatDate(endDateIso)}`;
  };

  const renderSection = (
    title: string,
    items: Array<{ row: HistoryRow; span: string; isActive?: boolean }>
  ) => {
    if (items.length === 0) return null;
    return (
      <HistorySection>
        <HistorySectionTitle>{title}</HistorySectionTitle>
        {items.map(({ row, span, isActive }) => (
          <HistoryItem key={`${row.effective_from}-${row.valueLabel}`} $active={!!isActive}>
            <HistoryValue>{row.valueLabel}</HistoryValue>
            <HistorySpan>{span}</HistorySpan>
          </HistoryItem>
        ))}
      </HistorySection>
    );
  };

  return (
    <HistoryListRoot>
      {renderSection(
        'Upcoming',
        future
          .map((_, i) => i)
          .reverse()
          .map((i) => {
            const globalIdx = asc.indexOf(future[i]);
            return { row: future[i], span: buildSpan(globalIdx, asc) };
          })
      )}
      {renderSection(
        'Active',
        activeIndex >= 0
          ? [
              {
                row: pastOrActive[activeIndex],
                span: buildSpan(asc.indexOf(pastOrActive[activeIndex]), asc),
                isActive: true,
              },
            ]
          : []
      )}
      {renderSection(
        'Past',
        pastOrActive
          .slice(0, activeIndex)
          .reverse()
          .map((row) => ({ row, span: buildSpan(asc.indexOf(row), asc) }))
      )}
    </HistoryListRoot>
  );
};

// ── Skeletons ──────────────────────────────────────────────────────────────

const SkeletonNav: React.FC = () => (
  <>
    {[0, 1, 2, 3, 4].map((i) => (
      <SkeletonNavRow key={i} />
    ))}
  </>
);

const SkeletonBody: React.FC = () => (
  <>
    <SkeletonHeading />
    {[0, 1, 2, 3].map((i) => (
      <SkeletonCard key={i}>
        <SkeletonText $width="40%" />
        <SkeletonText $width="80%" />
        <SkeletonInput />
      </SkeletonCard>
    ))}
  </>
);

// ── Helpers ────────────────────────────────────────────────────────────────

function initialBpInputValue(
  entry: BusinessParameterCatalogEntry,
  current?: BusinessParameter
): string {
  if (!current) return '';
  if (entry.value_type === 'map') return '';
  return String(current.value);
}

function initialBpMapValue(
  entry: BusinessParameterCatalogEntry,
  current?: BusinessParameter
): Record<string, string> {
  if (entry.value_type !== 'map') return {};
  if (current && typeof current.value === 'object' && current.value !== null) {
    const out: Record<string, string> = {};
    Object.entries(current.value as Record<string, unknown>).forEach(([k, v]) => {
      out[k] = String(v);
    });
    return out;
  }
  return {};
}

function shallowEqualStringMap(a: Record<string, string>, b: Record<string, string>): boolean {
  const seen: Record<string, true> = {};
  const all = Object.keys(a).concat(Object.keys(b));
  for (const k of all) {
    if (seen[k]) continue;
    seen[k] = true;
    if ((a[k] ?? '') !== (b[k] ?? '')) return false;
  }
  return true;
}

function formatBpValue(value: BusinessParameter['value']): string {
  if (value === null) return '—';
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '—';
    return entries.map(([k, v]) => `${k}: ${v}`).join(' · ');
  }
  return String(value);
}

function formatKpiValue(value: number, unit?: string, currencyCode?: string): string {
  const formatted = Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString();
  if (unit === 'currency') {
    return currencyCode ? `${currencyCode} ${formatted}` : formatted;
  }
  if (isPercentUnit(unit)) return `${formatted}%`;
  return formatted;
}

function isPercentUnit(unit?: string): boolean {
  if (!unit) return false;
  const u = unit.toLowerCase();
  return u === 'percent' || u === 'percentage' || u === '%';
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Today in YYYY-MM-DD, computed once at module load (panel is short-lived enough
// that we don't need to recompute around midnight edge cases).
const todayIso: string = (() => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
})();

function formatDate(iso: string): string {
  // Parse as local calendar date (YYYY-MM-DD), not UTC, so "2026-04-22"
  // renders as "Apr 22, 2026" regardless of timezone.
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function addDaysIso(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  date.setDate(date.getDate() + delta);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const ProfileModalStyles = createGlobalStyle`
  .modal-backdrop.show {
    opacity: 1 !important;
    background: ${({ theme }) => theme.av2.modalBackdrop} !important;
  }

  .av2-profile-modal {
    max-width: 1120px !important;
    margin: 1.5rem auto !important;
  }

  .av2-profile-modal-content {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
`;

const ModalPanel = styled.div`
  position: relative;
  background: ${({ theme }) => theme.av2.surface};
  color: ${({ theme }) => theme.av2.textPrimary};
  border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
  border-radius: 14px;
  box-shadow:
    0 24px 64px ${({ theme }) => theme.av2.shadowWarm},
    0 0 0 1px ${({ theme }) => theme.av2.primaryTint};
  overflow: hidden;
  max-height: min(860px, calc(100vh - 96px));
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    display: block;
    height: 3px;
    background: ${brandGradient};
    flex-shrink: 0;
  }

  select option {
    background: ${({ theme }) => theme.av2.surfaceElevated};
    color: ${({ theme }) => theme.av2.textPrimary};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 28px 10px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.av2.textPrimary};
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.av2.textSecondary};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.av2.textMuted};
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  &:hover { color: ${({ theme }) => theme.av2.textPrimary}; }
`;

const ModeTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 6px 28px 0;
  border-bottom: 1px solid ${({ theme }) => theme.av2.border};
`;

const ModeTab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 10px 4px;
  margin-right: 12px;
  font-size: 14px;
  color: ${(p) => (p.$active ? p.theme.av2.accentText : p.theme.av2.textMuted)};
  border-bottom: 2px solid ${(p) => (p.$active ? p.theme.av2.primary : 'transparent')};
  cursor: pointer;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 300px;
  gap: 20px;
  padding: 18px 28px 28px;
  min-height: 480px;
  height: min(640px, calc(100vh - 260px));
  overflow: hidden;
  flex: 1;
`;

const CategoryNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  padding-right: 8px;
  border-right: 1px solid ${({ theme }) => theme.av2.border};
`;

const CategoryItem = styled.button<{ $active: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? p.theme.av2.primaryBorder : 'transparent')};
  background: ${(p) => (p.$active ? p.theme.av2.primaryTintBg : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.av2.accentText : p.theme.av2.textSecondary)};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  text-align: left;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.av2.hoverBg}; }
  &:hover,
  &:focus-visible {
    outline: none;
  }
`;

const CompletionBadge = styled.span<{ $complete: boolean }>`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  background: ${(p) => (p.$complete ? p.theme.av2.successBg : p.theme.av2.surface)};
  color: ${(p) => (p.$complete ? p.theme.av2.success : p.theme.av2.textSecondary)};
`;

const CategoryBody = styled.div`
  overflow-y: auto;
  padding-right: 8px;
`;

const LocationScopePane = styled.aside`
  border-left: 1px solid ${({ theme }) => theme.av2.border};
  padding-left: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

const LocationPaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

const LocationPaneTitle = styled.h4`
  margin: 0;
  color: ${({ theme }) => theme.av2.textPrimary};
  font-size: 14px;
`;

const SelectedCount = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.av2.accentText};
  background: ${({ theme }) => theme.av2.primaryTintBg};
  border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 11px;
`;

const LocationSearchInput = styled.input`
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  background: ${({ theme }) => theme.av2.inputBg};
  color: ${({ theme }) => theme.av2.textPrimary};
  padding: 9px 10px;
  font-size: 13px;
  margin-bottom: 14px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.av2.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.av2.primaryTintStrong};
  }
`;

const LocationPaneSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    margin-bottom: 0;
  }
`;

const LocationPaneRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const LocationPaneLabel = styled.div`
  color: ${({ theme }) => theme.av2.textSecondary};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const ViewingLocationSelect = styled.select`
  width: 100%;
  margin-top: 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.av2.borderStrong};
  background: ${({ theme }) => theme.av2.surface};
  color: ${({ theme }) => theme.av2.textPrimary};
  padding: 9px 10px;
  font-size: 13px;
`;

const LocationHint = styled.div`
  margin-top: 8px;
  color: ${({ theme }) => theme.av2.textMuted};
  font-size: 12px;
  line-height: 1.4;
`;

const LocationActions = styled.div`
  display: flex;
  gap: 8px;
`;

const LocationAction = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.av2.accentText};
  font-size: 12px;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.av2.textPrimary};
  }
`;

const LocationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 4px 18px 0;
  scroll-padding-bottom: 18px;
`;

const LocationListItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 8px;
  color: ${({ theme }) => theme.av2.textSecondary};
  font-size: 12px;
  line-height: 1.3;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.av2.hoverBg};
  }

  input {
    margin-top: 1px;
    accent-color: ${({ theme }) => theme.av2.primary};
  }
`;

const LocationEmpty = styled.div`
  color: ${({ theme }) => theme.av2.textMuted};
  font-size: 12px;
  padding: 8px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.av2.accentText};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const CardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Card = styled.div<{ $highlight?: boolean }>`
  background: ${({ theme }) => theme.av2.surfaceElevated};
  border: 1px solid ${(p) => (p.$highlight ? p.theme.av2.accentText : p.theme.av2.border)};
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: ${(p) => (p.$highlight ? `0 0 0 3px ${p.theme.av2.primaryTintStrong}` : 'none')};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const CardHeading = styled.h5`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.av2.textPrimary};
  line-height: 1.3;
`;

const StatusChip = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 12px;
  background: ${(p) => (p.$active ? p.theme.av2.successBg : p.theme.av2.surface)};
  color: ${(p) => (p.$active ? p.theme.av2.success : p.theme.av2.textMuted)};
  white-space: nowrap;
`;

const Dot = styled.span<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? p.theme.av2.success : p.theme.av2.textLight)};
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.av2.textSecondary};
  line-height: 1.5;
`;

const CardEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 2px;
`;

const FieldGroup = styled.div<{ $inline?: boolean }>`
  display: ${(p) => (p.$inline ? 'flex' : 'flex')};
  flex-direction: ${(p) => (p.$inline ? 'row' : 'column')};
  align-items: ${(p) => (p.$inline ? 'center' : 'flex-start')};
  gap: ${(p) => (p.$inline ? '10px' : '6px')};
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.av2.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InputWithAffix = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const Affix = styled.span<{ $side: 'left' | 'right' }>`
  position: absolute;
  ${(p) => (p.$side === 'left' ? 'left: 10px;' : 'right: 10px;')}
  color: ${({ theme }) => theme.av2.textMuted};
  font-size: 13px;
  pointer-events: none;
`;

const UnitHint = styled.span`
  margin-left: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.av2.textMuted};
`;

const NumberInput = styled.input<{ $hasLeft?: boolean; $hasRight?: boolean }>`
  padding: 8px 10px;
  padding-left: ${(p) => (p.$hasLeft ? '40px' : '10px')};
  padding-right: ${(p) => (p.$hasRight ? '26px' : '10px')};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  background: ${({ theme }) => theme.av2.inputBg};
  color: ${({ theme }) => theme.av2.textPrimary};
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  font-variant-numeric: tabular-nums;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.av2.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.av2.primaryTintStrong};
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  background: ${({ theme }) => theme.av2.inputBg};
  color: ${({ theme }) => theme.av2.textPrimary};
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.av2.border};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.av2.inputBg};
`;

const SegmentedButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  background: ${(p) => (p.$active ? p.theme.av2.primaryTintBg : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.av2.textPrimary : p.theme.av2.textSecondary)};
  border: none;
  border-right: 1px solid ${({ theme }) => theme.av2.border};
  cursor: pointer;
  &:last-child { border-right: none; }
  &:hover:not(:disabled) {
    background: ${(p) => (p.$active ? p.theme.av2.primaryTintBg : p.theme.av2.hoverBg)};
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const SegmentedDot = styled.span<{ $active: boolean }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? p.theme.av2.success : p.theme.av2.success)};
`;

const MapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px 16px;
  width: 100%;
`;

const MapRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 10px;
`;

const MapRowLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.av2.accentTextLight};
`;

const DirtyFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  margin-top: 4px;
  background: ${({ theme }) => theme.av2.primaryTintBg};
  border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
  border-radius: 8px;
  flex-wrap: wrap;
`;

const DirtyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SaveBtn = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  background: ${({ theme }) => theme.av2.primary};
  color: ${({ theme }) => theme.av2.textOnAccent};
  cursor: pointer;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.av2.primaryHover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ResetBtn = styled.button`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  background: transparent;
  color: ${({ theme }) => theme.av2.textSecondary};
  cursor: pointer;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.av2.hoverBg}; color: ${({ theme }) => theme.av2.textPrimary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DateInput = styled.input`
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  background: ${({ theme }) => theme.av2.inputBg};
  color: ${({ theme }) => theme.av2.textPrimary};
  font-size: 13px;
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.av2.primary};
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const CardError = styled.div`
  color: ${({ theme }) => theme.av2.accentTextLight};
  font-size: 12px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.av2.errorBg};
  border-left: 3px solid ${({ theme }) => theme.av2.errorBorder};
  border-radius: 0 4px 4px 0;
`;

const PeriodOverview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PeriodChip = styled.button<{ $active: boolean; $set: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? p.theme.av2.primary : p.theme.av2.border)};
  background: ${(p) => (p.$active ? p.theme.av2.primaryTintBg : p.theme.av2.inputBg)};
  color: ${(p) => (p.$set ? p.theme.av2.textPrimary : p.theme.av2.textMuted)};
  cursor: pointer;
  text-align: left;
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.av2.primary}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const PeriodChipLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.av2.textMuted};
`;

const PeriodChipValue = styled.span`
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

const CardFooterLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.av2.border};
  flex-wrap: wrap;
`;

const CurrentValue = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.av2.textSecondary};
`;

const CurrentLabel = styled.span`
  color: ${({ theme }) => theme.av2.textMuted};
`;

const CurrentNumber = styled.span`
  color: ${({ theme }) => theme.av2.success};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const CurrentMeta = styled.span`
  color: ${({ theme }) => theme.av2.textMuted};
`;

const CurrentValueMuted = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.av2.textLight};
  font-style: italic;
`;

const HistoryToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.av2.accentText};
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  &:hover { color: ${({ theme }) => theme.av2.textPrimary}; }
`;

const HistoryWrap = styled.div`
  padding: 12px 14px;
  background: ${({ theme }) => theme.av2.inputBg};
  border: 1px solid ${({ theme }) => theme.av2.border};
  border-radius: 6px;
`;

const HistoryLoading = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.av2.textMuted};
`;

const HistoryListRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HistorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HistorySectionTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.av2.textMuted};
`;

const HistoryItem = styled.div<{ $active: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${(p) => (p.$active ? p.theme.av2.primaryTintBg : 'transparent')};
  border-left: 2px solid ${(p) => (p.$active ? p.theme.av2.success : p.theme.av2.border)};
  font-size: 12px;
`;

const HistoryValue = styled.span`
  color: ${({ theme }) => theme.av2.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const HistorySpan = styled.span`
  color: ${({ theme }) => theme.av2.textMuted};
  font-size: 11px;
`;

const HistoryEmpty = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.av2.textLight};
  font-style: italic;
`;

const ErrorBox = styled.div`
  margin: 8px 28px;
  padding: 10px 14px;
  border-left: 3px solid ${({ theme }) => theme.av2.errorBorder};
  background: ${({ theme }) => theme.av2.errorBg};
  border-radius: 0 6px 6px 0;
  color: ${({ theme }) => theme.av2.accentTextLight};
  font-size: 13px;
`;

// Skeletons

const shimmerBg = css`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.av2.hoverBg} 0%,
    ${({ theme }) => theme.av2.borderSubtle} 50%,
    ${({ theme }) => theme.av2.hoverBg} 100%
  );
  background-size: 800px 100%;
  border-radius: 6px;
`;

const SkeletonNavRow = styled.div`
  height: 38px;
  ${shimmerBg}
  animation: ${shimmer} 1.4s linear infinite;
`;

const SkeletonHeading = styled.div`
  width: 30%;
  height: 14px;
  margin: 4px 0 18px;
  ${shimmerBg}
  animation: ${shimmer} 1.4s linear infinite;
`;

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
  background: ${({ theme }) => theme.av2.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.av2.border};
  border-radius: 10px;
  margin-bottom: 14px;
`;

const SkeletonText = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${(p) => p.$width ?? '60%'};
  ${shimmerBg}
  animation: ${shimmer} 1.4s linear infinite;
`;

const SkeletonInput = styled.div`
  width: 200px;
  height: 36px;
  ${shimmerBg}
  animation: ${shimmer} 1.4s linear infinite;
`;

const CurrentValueSkeleton = styled.div`
  height: 12px;
  width: 100%;
  box-sizing: border-box;
  ${shimmerBg}
  animation: ${shimmer} 1.4s linear infinite;
`;

export default BusinessProfilePanel;
